import { useEffect } from 'react'
import { Card } from '@/components/common'
import { Settings as SettingsIcon, Database, ToggleLeft, ToggleRight, Info } from 'lucide-react'
import { useSettings, setGlobalUseMockData } from '@/contexts/SettingsContext'
import clsx from 'clsx'

export default function Settings() {
  const { settings, updateSettings } = useSettings()

  // Sync mock data setting to global state for API services
  useEffect(() => {
    setGlobalUseMockData(settings.useMockData)
  }, [settings.useMockData])

  const handleMockDataToggle = () => {
    updateSettings({ useMockData: !settings.useMockData })
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary-500 to-accent-cyan flex items-center justify-center">
          <SettingsIcon size={20} className="text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-surface-100">Settings</h1>
          <p className="text-surface-400">Configure application preferences</p>
        </div>
      </div>

      {/* Data Source Settings */}
      <Card title="Data Source" subtitle="Configure how data is loaded">
        <div className="space-y-4">
          {/* Mock Data Toggle */}
          <div className="flex items-center justify-between p-4 bg-surface-900 rounded-lg">
            <div className="flex items-center gap-3">
              <Database size={20} className="text-surface-400" />
              <div>
                <h3 className="text-surface-200 font-medium">Use Mock Data</h3>
                <p className="text-surface-500 text-sm">
                  When enabled, the dashboard will use demo/mock data instead of calling the API.
                  Useful for testing UI without a running backend.
                </p>
              </div>
            </div>
            <button
              onClick={handleMockDataToggle}
              className={clsx(
                'flex-shrink-0 w-14 h-7 rounded-full transition-colors duration-200 relative',
                settings.useMockData ? 'bg-primary-500' : 'bg-surface-700'
              )}
            >
              <span
                className={clsx(
                  'absolute w-5 h-5 bg-white rounded-full top-1 transition-all duration-200',
                  settings.useMockData ? 'left-8' : 'left-1'
                )}
              />
              {settings.useMockData ? (
                <ToggleRight size={14} className="absolute right-1.5 top-1.5 text-white" />
              ) : (
                <ToggleLeft size={14} className="absolute left-1.5 top-1.5 text-surface-400" />
              )}
            </button>
          </div>

          {/* Status Info */}
          <div className={clsx(
            'flex items-start gap-3 p-4 rounded-lg',
            settings.useMockData ? 'bg-status-warning/10 border border-status-warning/30' : 'bg-status-healthy/10 border border-status-healthy/30'
          )}>
            <Info size={18} className={settings.useMockData ? 'text-status-warning' : 'text-status-healthy'} />
            <div>
              <h4 className={clsx(
                'font-medium',
                settings.useMockData ? 'text-status-warning' : 'text-status-healthy'
              )}>
                {settings.useMockData ? 'Mock Data Mode Active' : 'Live API Mode Active'}
              </h4>
              <p className="text-surface-400 text-sm mt-1">
                {settings.useMockData
                  ? 'All data displayed is simulated. Enable this to test UI functionality without API access.'
                  : 'Data is being fetched from the live API. If API calls fail, fallback mock data will be used automatically.'
                }
              </p>
            </div>
          </div>
        </div>
      </Card>

      {/* API Status */}
      <Card title="API Status" subtitle="Current connection status">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { name: 'Prediction API', endpoint: '/api/v2/predict', status: 'connected' },
            { name: 'Dashboard API', endpoint: '/api/v2/dashboard', status: 'connected' },
            { name: 'Data API', endpoint: '/api/v2/data', status: 'connected' },
            { name: 'Models API', endpoint: '/api/v2/models', status: 'connected' },
            { name: 'Reports API', endpoint: '/api/v2/reports', status: 'connected' },
            { name: 'WebSocket', endpoint: '/ws', status: 'connected' },
          ].map((api) => (
            <div key={api.name} className="p-3 bg-surface-900 rounded-lg">
              <div className="flex items-center justify-between mb-1">
                <span className="text-surface-200 font-medium text-sm">{api.name}</span>
                <span className={clsx(
                  'w-2 h-2 rounded-full',
                  api.status === 'connected' ? 'bg-status-healthy' : 'bg-status-critical'
                )} />
              </div>
              <code className="text-xs text-surface-500">{api.endpoint}</code>
            </div>
          ))}
        </div>
      </Card>
    </div>
  )
}
